import { TZDate } from "@date-fns/tz";
import { darkenHexColor, lightenHexColor } from "./color";
import { formatGtfsStartDate } from "./liveTripTiming";
export { normalizeRouteColor } from "./routeDisplay.js";

export function getTripTransportMode(routeType) {
  switch (Number(routeType)) {
    case 2:
      return "train";
    case 4:
      return "ferry";
    case 5:
      return "cable_car";
    default:
      return "bus";
  }
}

export function getDepartedStopRouteColor(color, isDark = false) {
  return isDark ? darkenHexColor(color, 0.4) : lightenHexColor(color, 0.5);
}

export function getRouteLineContrastColor(color, isDark = false) {
  return isDark ? lightenHexColor(color, 0.65) : lightenHexColor(color, 0.78);
}

export function getWellPlatformDisplay(stopId) {
  if (!stopId?.startsWith("WELL")) {
    return null;
  }

  const platform = String(stopId).replace(/^WELL/, "");
  return platform || null;
}

export function getTripPagePlatformLookupStartDate(dateStr, timeStr, timeZone) {
  if (!dateStr || !timeStr) {
    return null;
  }

  const [year, month, day] = String(dateStr)
    .split("-")
    .map((part) => Number(part));
  const [hour, minute, second = 0] = String(timeStr)
    .split(":")
    .map((part) => Number(part));

  if (
    [year, month, day, hour, minute, second].some(
      (part) => !Number.isFinite(part),
    )
  ) {
    return null;
  }

  const stopDateTime = new TZDate(
    year,
    month - 1,
    day,
    hour % 24,
    minute,
    second,
    0,
    timeZone,
  );

  stopDateTime.setTime(stopDateTime.getTime() - 3 * 60 * 60 * 1000);

  const lookupYear = stopDateTime.getFullYear();
  const lookupMonth = String(stopDateTime.getMonth() + 1).padStart(2, "0");
  const lookupDay = String(stopDateTime.getDate()).padStart(2, "0");

  return `${lookupYear}-${lookupMonth}-${lookupDay}`;
}

export function formatGtfsTimeForDisplay(time) {
  const [hourString, minuteString, secondString] = String(time ?? "").split(":");

  if (
    !/^\d+$/.test(hourString) ||
    !/^\d+$/.test(minuteString) ||
    !/^\d+$/.test(secondString)
  ) {
    return "12:00 am";
  }

  const hour = Number(hourString);
  const minute = Number(minuteString);
  const second = Number(secondString);

  if (
    !Number.isSafeInteger(hour) ||
    !Number.isSafeInteger(minute) ||
    !Number.isSafeInteger(second) ||
    minute > 59 ||
    second > 59
  ) {
    return "12:00 am";
  }

  const clockHour = hour % 24;
  const displayHour = clockHour % 12 || 12;
  const meridiem = clockHour < 12 ? "am" : "pm";

  return `${displayHour}:${String(minute).padStart(2, "0")} ${meridiem}`;
}

export function isTripCancellationAlert(alert) {
  const effect = normalizeAlertEffect(alert?.effect);
  const header = String(alert?.header_text || "");
  const description = String(alert?.description_text || "");

  return (
    effect === "NO_SERVICE" ||
    /\bservice\s+cancellation\b/i.test(header) ||
    /\btrip\s+cancell?ed\b/i.test(`${header} ${description}`)
  );
}

export function buildTripCancellationDescription({
  departureTime,
  origin,
  destination,
}) {
  if (!departureTime || !origin || !destination) {
    return "This trip has been cancelled.";
  }

  return `The ${departureTime} service from ${origin} to ${destination} has been cancelled.`;
}

function firstAlertTranslation(value) {
  if (typeof value === "string") {
    return value;
  }

  const translation = value?.translation;
  return Array.isArray(translation) ? translation[0]?.text || null : null;
}

function alertScalarNumber(value) {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === "string" && value.trim()) {
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
  }

  if (value && typeof value.toNumber === "function") {
    const number = value.toNumber();
    return Number.isFinite(number) ? number : null;
  }

  if (value && typeof value.low === "number") {
    return Number.isFinite(value.low) ? value.low : null;
  }

  return null;
}

function normalizeAlertEffect(value) {
  const numeric = alertScalarNumber(value);
  if (numeric === 1) {
    return "NO_SERVICE";
  }

  return String(value || "").toUpperCase();
}

function normalizeAlertTimestamp(value) {
  if (value === undefined || value === null || value === "") {
    return null;
  }

  const numeric = alertScalarNumber(value);
  if (numeric !== null) {
    const milliseconds = Math.abs(numeric) < 1e12 ? numeric * 1000 : numeric;
    const date = new Date(milliseconds);
    return Number.isNaN(date.getTime()) ? null : date.toISOString();
  }

  const date = new Date(String(value));
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

function uniqueAlertIds(values) {
  return Array.from(
    new Set(
      values
        .flatMap((value) => (Array.isArray(value) ? value : [value]))
        .filter((value) => value !== undefined && value !== null && value !== "")
        .map((value) => String(value)),
    ),
  );
}

function normalizeAlertPeriod(period) {
  return {
    start: normalizeAlertTimestamp(period?.start),
    end: normalizeAlertTimestamp(period?.end),
  };
}

function normalizeAlertServiceDay(value) {
  const text = String(value || "").trim();
  if (/^\d{8}$/.test(text)) {
    return `${text.slice(0, 4)}-${text.slice(4, 6)}-${text.slice(6, 8)}`;
  }

  return /^\d{4}-\d{2}-\d{2}$/.test(text) ? text : null;
}

function getAlertDateKey(value) {
  const normalized = normalizeAlertTimestamp(value);
  if (!normalized) {
    return null;
  }

  const parts = new Intl.DateTimeFormat("en-NZ", {
    timeZone: "Pacific/Auckland",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date(normalized));
  const values = Object.fromEntries(
    parts
      .filter(({ type }) => type !== "literal")
      .map(({ type, value: partValue }) => [type, partValue]),
  );

  return `${values.year}-${values.month}-${values.day}`;
}

function getAlertInformedEntities(alert) {
  const nestedAlert = alert?.alert;
  return Array.isArray(nestedAlert?.informedEntity)
    ? nestedAlert.informedEntity
    : Array.isArray(alert?.informedEntity)
      ? alert.informedEntity
      : [];
}

export function normalizeTripPageAlert(alert) {
  const nestedAlert =
    alert?.alert && typeof alert.alert === "object" ? alert.alert : null;
  const informedEntities = getAlertInformedEntities(alert);
  const activePeriod =
    alert?.active_period ??
    alert?.activePeriod ??
    nestedAlert?.active_period ??
    nestedAlert?.activePeriod;
  const routeIdsFromEntities = informedEntities.map(
    (entity) => entity?.routeId,
  );
  const stopIdsFromEntities = informedEntities.map((entity) => entity?.stopId);
  const tripIdsFromEntities = informedEntities.map(
    (entity) => entity?.trip?.tripId || entity?.tripId,
  );

  return {
    ...alert,
    alert_id: alert?.alert_id ?? alert?.id ?? nestedAlert?.alert_id ?? null,
    id: alert?.id ?? alert?.alert_id ?? null,
    header_text:
      alert?.header_text ??
      firstAlertTranslation(alert?.headerText) ??
      firstAlertTranslation(nestedAlert?.headerText) ??
      firstAlertTranslation(nestedAlert?.header_text) ??
      "",
    description_text:
      alert?.description_text ??
      firstAlertTranslation(alert?.descriptionText) ??
      firstAlertTranslation(nestedAlert?.descriptionText) ??
      firstAlertTranslation(nestedAlert?.description_text) ??
      "",
    url:
      firstAlertTranslation(alert?.url) ??
      firstAlertTranslation(nestedAlert?.url) ??
      firstAlertTranslation(nestedAlert?.url_text) ??
      null,
    effect: normalizeAlertEffect(alert?.effect ?? nestedAlert?.effect),
    active_period: Array.isArray(activePeriod)
      ? activePeriod.map(normalizeAlertPeriod)
      : [],
    route_ids: uniqueAlertIds([
      alert?.route_ids,
      alert?.routeIds,
      nestedAlert?.route_ids,
      routeIdsFromEntities,
    ]),
    stop_ids: uniqueAlertIds([
      alert?.stop_ids,
      alert?.stopIds,
      nestedAlert?.stop_ids,
      stopIdsFromEntities,
    ]),
    trip_ids: uniqueAlertIds([
      alert?.trip_ids,
      alert?.tripIds,
      alert?.trip_id,
      alert?.tripId,
      nestedAlert?.trip_ids,
      nestedAlert?.tripId,
      tripIdsFromEntities,
    ]),
  };
}

function isTripPageAlertOnServiceDay(alert, serviceDayStr) {
  const serviceDay = normalizeAlertServiceDay(serviceDayStr);
  if (!serviceDay) {
    return false;
  }

  const activePeriods = Array.isArray(alert.active_period)
    ? alert.active_period
    : [];
  if (activePeriods.length === 0) {
    return true;
  }

  return activePeriods.some((period) => {
    const startDay = period.start ? getAlertDateKey(period.start) : null;
    const endDay = period.end ? getAlertDateKey(period.end) : null;

    return (
      (!startDay || serviceDay >= startDay) &&
      (!endDay || serviceDay <= endDay)
    );
  });
}

function isMatchingTripPageId(candidateTripId, tripId) {
  const candidate = String(candidateTripId || "");
  const requested = String(tripId || "");

  return (
    candidate === requested ||
    (requested !== "" && candidate.startsWith(`${requested}_`))
  );
}

function isTripPageAlertInScope(alert, { stopId, tripId } = {}) {
  if (alert.trip_ids.length > 0) {
    return (
      !tripId ||
      alert.trip_ids.some((candidateTripId) =>
        isMatchingTripPageId(candidateTripId, tripId),
      )
    );
  }

  if (!stopId || alert.stop_ids.length === 0) {
    return true;
  }

  return alert.stop_ids.includes(String(stopId));
}

export function filterTripPageAlerts(
  alerts,
  { serviceDayStr, stopId, tripId } = {},
) {
  return (Array.isArray(alerts) ? alerts : [])
    .map(normalizeTripPageAlert)
    .filter(
      (alert) =>
        isTripPageAlertOnServiceDay(alert, serviceDayStr) &&
        isTripPageAlertInScope(alert, { stopId, tripId }),
    );
}

export function getTripPageAlertViewModel({
  alerts,
  departureTime,
  destination,
  origin,
  serviceDayStr,
  stopId,
  tripId,
  tripCancellations,
} = {}) {
  const filteredAlerts = filterTripPageAlerts(alerts, {
    serviceDayStr,
    stopId,
    tripId,
  });
  const retainedCancellation = (Array.isArray(tripCancellations)
    ? tripCancellations
    : []
  )
    .map((cancellation) => ({
      ...normalizeTripPageAlert(cancellation),
      service_date: normalizeAlertServiceDay(cancellation?.service_date),
    }))
    .find(
      (cancellation) =>
        cancellation.service_date === normalizeAlertServiceDay(serviceDayStr) &&
        isTripPageAlertInScope(cancellation, { tripId }),
    );
  const cancellationAlert =
    filteredAlerts.find(isTripCancellationAlert) ||
    retainedCancellation ||
    null;

  return {
    cancellationAlert,
    cancellationDescription: buildTripCancellationDescription({
      departureTime,
      origin,
      destination,
    }),
    filteredAlerts,
    isTripCancelled: Boolean(cancellationAlert),
    nonCancellationAlerts: filteredAlerts.filter(
      (alert) => !isTripCancellationAlert(alert),
    ),
  };
}

export function getTripServiceDayForStop({ date, stopTimesTrip, currentStop }) {
  if (!date || !stopTimesTrip?.length) return date ?? null;

  const firstHr = Number(stopTimesTrip[0].departure_time.split(":")[0]);
  const currentHr = currentStop
    ? Number(currentStop.departure_time.split(":")[0])
    : firstHr;

  if (firstHr < 24 && currentHr >= 24) {
    const nzBase = new TZDate(date, "Pacific/Auckland");
    nzBase.setDate(nzBase.getDate() - 1);
    const y = nzBase.getFullYear();
    const m = String(nzBase.getMonth() + 1).padStart(2, "0");
    const d = String(nzBase.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }

  return date;
}

export function getTripBaseDate({ date, serviceDayStr, isServiceDayForQuery }) {
  if (!isServiceDayForQuery) return null;
  return serviceDayStr ?? date ?? null;
}

export function getTripEndpointUrls({ siteUrl, tripId }) {
  const encodedTripId = encodeURIComponent(tripId);

  return {
    canonicalUrl: `${siteUrl}/trip/${encodedTripId}`,
    shareImage: `${siteUrl}/api/ogimage/trip/${encodedTripId}`,
  };
}

export function getLiveTripShareUrl({ siteUrl, asPath, startDate }) {
  const url = new URL(asPath, siteUrl);
  const date = formatGtfsStartDate(startDate);

  if (date) {
    url.searchParams.set("date", date);
  }

  return url.toString();
}

export function buildTripPageTitle({
  mode,
  routeLabel,
  schoolBusSchool,
  tripTitle,
}) {
  if (mode === "bus") {
    return `Route ${routeLabel} (${tripTitle}${
      tripTitle === "School Bus" ? ": " + schoolBusSchool : ""
    }) - Catchy`;
  }

  return `${routeLabel} - ${tripTitle} - Catchy`;
}

export function buildTripPageDescription({
  mode,
  routeLabel,
  schoolBusSchool,
  tripTitle,
}) {
  const destination =
    tripTitle === "School Bus" && schoolBusSchool
      ? `${tripTitle}: ${schoolBusSchool}`
      : tripTitle;
  const routePrefix = mode === "bus" ? `route ${routeLabel}` : routeLabel;

  return `Follow ${routePrefix} to ${destination} live, including upcoming stops, expected departure times, and timetable details.`;
}
